import React, { useState } from 'react'
import { Bell, X, Check, Clock, AlertCircle, Info, ChevronDown } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNotifications } from '../../hooks/useNotifications'
import NotificationItem from './NotificationItem'
import LoadingSpinner from '../common/LoadingSpinner'

const NotificationCenter: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [showAll, setShowAll] = useState(false)

  const {
    notifications,
    unreadCount,
    totalCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useNotifications({
    pageSize: showAll ? 50 : 10,
    enablePagination: showAll
  })

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 hover:bg-cream-100 dark:hover:bg-ink-700 rounded-caderno-sm relative transition-colors"
      >
        <Bell className="w-5 h-5 text-ink-500 dark:text-ink-300" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-blood-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="md:absolute fixed mt-2 bg-surface dark:bg-ink-700 rounded-caderno-sm shadow-caderno-xl border border-cream-400 dark:border-ink-500 z-50 max-h-[80vh] overflow-hidden notification-container"
            >
              <div className="px-4 py-3 border-b border-cream-300 dark:border-ink-500">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-display text-[16px] font-semibold tracking-[-0.01em] text-ink-900 dark:text-cream-50 truncate">Notificações</h3>
                  {unreadCount > 0 && (
                    <button
                      onClick={() => markAllAsRead()}
                      className="text-xs sm:text-sm text-terra-500 dark:text-terra-300 hover:text-terra-700 dark:hover:text-terra-300 whitespace-nowrap flex-shrink-0"
                    >
                      Marcar todas como lidas
                    </button>
                  )}
                </div>
              </div>

              <div className="max-h-[calc(80vh-120px)] overflow-y-auto">
                {isLoading ? (
                  <div className="p-8 text-center">
                    <LoadingSpinner size="sm" text="Carregando notificações..." />
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="p-8 text-center">
                    <Bell className="w-12 h-12 text-ink-300 dark:text-ink-400 mx-auto mb-3" />
                    <p className="text-ink-400 dark:text-ink-300">Nenhuma notificação</p>
                  </div>
                ) : (
                  <>
                    {notifications.map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        id={notification.id}
                        type={notification.type}
                        title={notification.title}
                        message={notification.message}
                        read={notification.read}
                        createdAt={notification.created_at}
                        actionUrl={notification.action_url}
                        actionLabel={notification.action_label}
                        onMarkAsRead={markAsRead}
                        onDelete={deleteNotification}
                      />
                    ))}

                    {/* Load More / Show All Toggle */}
                    {(hasNextPage || totalCount > notifications.length) && (
                      <div className="p-4 border-t border-cream-400 dark:border-ink-500">
                        {!showAll ? (
                          <button
                            onClick={() => setShowAll(true)}
                            className="w-full flex items-center justify-center space-x-2 text-sm text-terra-500 dark:text-terra-300 hover:text-terra-700 dark:hover:text-terra-300 py-2"
                          >
                            <span>Ver todas as notificações ({totalCount})</span>
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        ) : hasNextPage && (
                          <button
                            onClick={() => fetchNextPage()}
                            disabled={isFetchingNextPage}
                            className="w-full flex items-center justify-center space-x-2 text-sm text-terra-500 dark:text-terra-300 hover:text-terra-700 dark:hover:text-terra-300 py-2 disabled:opacity-50"
                          >
                            {isFetchingNextPage ? (
                              <>
                                <LoadingSpinner size="xs" />
                                <span>Carregando...</span>
                              </>
                            ) : (
                              <>
                                <span>Carregar mais</span>
                                <ChevronDown className="w-4 h-4" />
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default NotificationCenter